<template>
  <SoWidget
    no-border
    no-padding
    :loading="!current.meterpoints.isFetched"
    data-testid="submit-readings-widget"
  >
    <section v-if="current.meterpoints.isFetched" class="submit-readings">
      <SoAlert
        v-if="firstReadMessages.length > 0"
        class="mb-5"
        type="warning"
        data-testid="first-read-alert"
      >
        <p v-if="isDualFuel">
          Gas and electricity have different periods in which the meter reading
          should be submitted as a FIRST reading.
        </p>
        <p v-for="(firstReadMessage, index) in firstReadMessages" :key="index">
          {{ firstReadMessage }}
        </p>
      </SoAlert>
      <SoRow>
        <SoCol>
          <SoDatePicker
            v-model="readingDate"
            label="Reading date"
            data-testid="reading-date"
            :max-date="new Date()"
          />
        </SoCol>
        <SoCol class="d-flex flex-column align-end justify-end">
          <SoButton
            prepend-icon="camera"
            title="Photo Received"
            large
            @click="isPhotoReceivedModalOpen = true"
          />
        </SoCol>
      </SoRow>
      <SoAlert
        v-if="photoRequestTickets.length"
        class="my-5"
        title="Open photo request ticket"
        type="warning"
        >There is an open photo request ticket on this account please ensure you
        have the correct photos and click the
        <span class="font-weight-medium"> Photos Received </span> button.
      </SoAlert>
      <SoRow>
        <SoCol
          v-for="(meterpoint, index) in current.meterpoints.data"
          :key="index"
          :default-width="6"
          :lg-and-down-width="usedInModal ? 6 : 12"
        >
          <SoWidget
            data-testid="meterpoint-widget-submit-reading"
            class="d-flex w-100 flex-column"
          >
            <MeterpointHeader :meterpoint="meterpoint" class="mb-6" />

            <MeterReadingForm
              v-for="(meter, meterIndex) in meterpoint.meters"
              :key="meterIndex"
              :meterpoint="meterpoint"
              :meter="meter"
              :reading-date="readingDate"
              :should-submit-as-first-read="shouldSubmitAsFirstRead(meterpoint)"
            />
            <div v-if="!meterpoint.meters?.length">
              <SoAlert
                class="mt-5"
                title="No Meter Technical Details"
                type="error"
              >
                <p class="mt-2">
                  We haven't received the Meter Technical Details (MTDs) from
                  the previous supplier yet.
                </p>
                <p class="mt-2">
                  This can sometimes take upto 2 weeks after Supply Start Date
                  (SSD).
                </p>
                <p class="mt-2">
                  If they do not appear shortly, log on to Junifer to submit the
                  meter reading.
                </p>
              </SoAlert>
            </div>
          </SoWidget>
        </SoCol>
      </SoRow>
      <MeterReadingPhotoReceivedModal v-model="isPhotoReceivedModalOpen" />
    </section>
  </SoWidget>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import dayjs from "dayjs"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { formatDate } from "@/helpers/dateHelper"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import SoAlert from "@/components/SoAlert.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import MeterpointHeader from "@/views/customer/components/MeterpointHeader.vue"
  import MeterReadingPhotoReceivedModal from "./MeterReadingPhotoReceivedModal.vue"
  import MeterReadingForm from "./MeterReadingForm.vue"

  export default defineComponent({
    components: {
      SoAlert,
      SoWidget,
      SoDatePicker,
      SoButton,
      SoRow,
      SoCol,
      MeterReadingForm,
      MeterReadingPhotoReceivedModal,
      MeterpointHeader,
    },
    props: {
      usedInModal: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        formatDate,
        readingDate: new Date(),
        current: useCurrentAccountStore(),
        isPhotoReceivedModalOpen: false,
      }
    },
    computed: {
      photoRequestTickets() {
        return this.current.juniferTickets.data.filter(
          (ticket) => ticket.isPhotoRequestTicket,
        )
      },
      showFirstReadAlert() {
        return this.current.meterpoints.data.some((meterpoint) =>
          this.shouldSubmitAsFirstRead(meterpoint),
        )
      },
      isDualFuel() {
        const hasElectricity = this.current.meterpoints.data.some(
          (meterpoint) => meterpoint.isElectricity,
        )
        const hasGas = this.current.meterpoints.data.some(
          (meterpoint) => !meterpoint.isElectricity,
        )

        return hasElectricity && hasGas
      },
      firstReadMessages() {
        if (!this.showFirstReadAlert) return []

        return this.current.meterpoints.data.map((meterpoint) => {
          const fuelType = meterpoint.isElectricity ? "electricity" : "gas"
          const meterpointIdentifier = `${meterpoint.meterpointType} - ${meterpoint.identifier}`
          const startDate = this.formatDate(meterpoint.firstReadWindowStart)
          const endDate = this.formatDate(meterpoint.firstReadWindowEnd)

          return `The ${fuelType} (${meterpointIdentifier}) opening reading can be submitted from ${startDate} to ${endDate}`
        })
      },
    },
    methods: {
      shouldSubmitAsFirstRead(meterpoint: IMeterpoint) {
        return (
          !!this.readingDate &&
          meterpoint.inFirstReadPeriod(dayjs(this.readingDate)) &&
          !meterpoint.readings.some((reading) => reading.isOfTypeFirst)
        )
      },
    },
  })
</script>

<style scoped>
  .submit-readings {
    line-height: 1.6;
  }
</style>
